import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { dt } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Building2, Bot, BookOpen, UsersRound, Plus, Trash2, Truck, Repeat, ScrollText, MessageSquareText, Copy, Check,
  CreditCard, FileText, ShieldAlert, KeyRound, ClipboardCheck, ArrowRight, Palette, Image as ImageIcon, Bell,
  Smartphone, RotateCcw, Volume2, BellRing, Landmark, ShieldCheck, BarChart3, Upload, MessageCircleQuestion,
} from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import { subscribePush, unsubscribePush } from "@/lib/push";

const SUGERENCIAS_COLOR = ["#E29AA0", "#7C5CFC", "#1F8A70", "#E0764E", "#2563EB", "#111827"];

const DIAS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];

function minutesToHM(min) {
  min = min || 0;
  return { h: Math.floor(min / 60), m: min % 60 };
}

export default function Settings() {
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [s, setS] = useState(null);
  const [kb, setKb] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [kbForm, setKbForm] = useState(null);
  const [userForm, setUserForm] = useState(null);
  const [demoState, setDemoState] = useState(null);
  const [activarDemoOpen, setActivarDemoOpen] = useState(false);
  const [activarDemoText, setActivarDemoText] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [browserPerm, setBrowserPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [restaurarMarcaOpen, setRestaurarMarcaOpen] = useState(false);
  // Fix N (2026-09-22): preferencias del equipo y actividad por persona.
  const [equipoPrefs, setEquipoPrefs] = useState(null);
  const [actividad, setActividad] = useState(null);
  const [rangoActividad, setRangoActividad] = useState(7);
  const loadEquipo = () => api.get("/equipo/preferencias").then((r) => setEquipoPrefs(r.data)).catch(() => {});
  const loadActividad = (dias) => {
    const hasta = new Date();
    const desde = new Date(Date.now() - (dias - 1) * 86400000);
    const f = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Santo_Domingo" });
    return api.get("/equipo/actividad", { params: { desde: f(desde), hasta: f(hasta) } }).then((r) => setActividad(r.data)).catch(() => {});
  };
  useEffect(() => { loadEquipo(); }, []);
  useEffect(() => { loadActividad(rangoActividad); }, [rangoActividad]);
  const setEquipoPref = async (patch) => {
    try {
      const { data } = await api.put("/equipo/preferencias", patch);
      setEquipoPrefs(data);
      setS((prev) => (prev ? { ...prev, asignacion: { ...(prev.asignacion || {}), ...data } } : prev));
      toast.success("Preferencia guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const loadChecklist = () => api.get("/onboarding-checklist").then((r) => setChecklist(r.data));
  const loadNotifPrefs = () => api.get("/notifications/preferencias").then((r) => setNotifPrefs(r.data));

  const load = async () => {
    const [a, b, c, d, e, f] = await Promise.all([api.get("/settings"), api.get("/knowledge"), api.get("/users"), api.get("/audit-logs"), api.get("/plantillas"), api.get("/admin/demo-seed")]);
    setS(a.data); setKb(b.data); setUsers(c.data); setAudit(d.data); setTemplates(e.data); setDemoState(f.data);
    loadChecklist();
    loadNotifPrefs();
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => setPushSubscribed(!!sub)).catch(() => {});
    }
  }, []);

  const desactivarDemo = async () => {
    try { const { data } = await api.post("/admin/demo-seed/desactivar"); setDemoState(data); toast.success("Datos de demostración desactivados"); loadChecklist(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const activarDemo = async () => {
    try {
      const { data } = await api.post("/admin/demo-seed/activar", { confirmar: activarDemoText });
      setDemoState(data); toast.success("Datos de demostración reactivados"); setActivarDemoOpen(false); setActivarDemoText(""); loadChecklist();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const resetPassword = async (u) => {
    try { const { data } = await api.post(`/users/${u.id}/resetear-password`); setResetResult({ user: u, password: data.password_temporal }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const saveSettings = async () => {
    try { await api.put("/settings", s); toast.success("Configuración guardada"); loadChecklist(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setHorario = (dia, field, val) => setS({ ...s, horarios: { ...s.horarios, [dia]: { ...s.horarios?.[dia], [field]: val } } });

  // Fix K (2026-09-22): zonas con sus reglas de cálculo completas + probador.
  const ZONA_VACIA = { zona: "", tarifa: 0, sectores: [], gratis_desde: null, recargo_contra_entrega: 0,
    permite_contra_entrega: true, tipo_entrega: "mensajero", tiempo_entrega: "", activa: true };
  const nuevoIdZona = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `z-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const zonas = s?.zonas_envio_lista || [];
  const setZonas = (zs) => setS({ ...s, zonas_envio_lista: zs });
  const addZona = (preset = {}) => setZonas([...zonas, { ...ZONA_VACIA, id: nuevoIdZona(), ...preset }]);
  const updateZona = (i, field, val) => setZonas(zonas.map((z, idx) => idx === i ? { ...z, [field]: val } : z));
  const removeZona = (i) => setZonas(zonas.filter((_, idx) => idx !== i));
  const [sectoresTexto, setSectoresTexto] = useState({});
  const zonaKey = (z, i) => z.id || `fila-${i}`;
  const partirSectores = (txt) => (txt || "").split(",").map((x) => x.trim()).filter(Boolean);
  const zonasConTextoPendiente = () => zonas.map((z, i) => (sectoresTexto[zonaKey(z, i)] !== undefined
    ? { ...z, sectores: partirSectores(sectoresTexto[zonaKey(z, i)]) } : z));
  const commitSectores = (i, z) => {
    const k = zonaKey(z, i);
    if (sectoresTexto[k] === undefined) return;
    updateZona(i, "sectores", partirSectores(sectoresTexto[k]));
    setSectoresTexto((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };
  const guardarZonas = async () => {
    try {
      const { data } = await api.put("/settings", { ...s, zonas_envio_lista: zonasConTextoPendiente() });
      setS(data); setSectoresTexto({}); toast.success("Zonas guardadas"); loadChecklist();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const [pruebaEnvio, setPruebaEnvio] = useState({ lugar: "", subtotal: "", metodo_pago: "transferencia" });
  const [pruebaEnvioRes, setPruebaEnvioRes] = useState(null);
  const probarEnvio = async () => {
    try {
      const { data } = await api.post("/envios/cotizar", {
        lugar: pruebaEnvio.lugar, subtotal: Number(pruebaEnvio.subtotal) || 0,
        metodo_pago: pruebaEnvio.metodo_pago, zonas: zonasConTextoPendiente(),
      });
      setPruebaEnvioRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo calcular"); }
  };

  const seg = s?.seguimientos || { activo: false, cantidad: 1, esperas_minutos: [240, 1440], plantillas: [], respetar_horario: true };
  const setSeg = (patch) => setS({ ...s, seguimientos: { ...seg, ...patch } });
  const setEsperaHM = (idx, field, val) => {
    const esperas = [...(seg.esperas_minutos || [240, 1440])];
    const { h, m } = minutesToHM(esperas[idx]);
    const nh = field === "h" ? Number(val) : h;
    const nm = field === "m" ? Number(val) : m;
    esperas[idx] = nh * 60 + nm;
    setSeg({ esperas_minutos: esperas });
  };
  const setPlantilla = (idx, val) => {
    const plantillas = [...(seg.plantillas || [])];
    plantillas[idx] = val;
    setSeg({ plantillas });
  };

  // Fix N (2026-09-22): preguntas por responder, sugeridas, categorías y carga masiva.
  const KB_CATEGORIAS = ["Envíos", "Pagos", "Productos", "Horarios y ubicación", "Cambios y devoluciones", "Pedidos", "General"];
  const KB_ORIGEN = { manual: "Manual", ejemplo_marcado: "Ejemplo marcado", pregunta_de_cliente: "Pregunta de cliente", carga_masiva: "Carga masiva", sugerida: "Sugerida" };
  const [kbPend, setKbPend] = useState([]);
  const [kbQuery, setKbQuery] = useState("");
  const [kbCat, setKbCat] = useState("todas");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTexto, setBulkTexto] = useState("");
  const [bulkItems, setBulkItems] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const loadKbPend = () => api.get("/knowledge/pendientes").then((r) => setKbPend(r.data)).catch(() => {});
  useEffect(() => { loadKbPend(); }, []);
  const saveKb = async () => {
    try {
      if (kbForm.pendiente_id) {
        await api.post(`/knowledge/pendientes/${kbForm.pendiente_id}/responder`, { pregunta: kbForm.pregunta, respuesta: kbForm.respuesta, categoria: kbForm.categoria || "General" });
      } else if (kbForm.id) await api.put(`/knowledge/${kbForm.id}`, kbForm);
      else await api.post("/knowledge", kbForm);
      toast.success(kbForm.pendiente_id ? "Respuesta guardada: el agente ya la usa" : "Guardado"); setKbForm(null); load(); loadKbPend();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delKb = async (k) => { await api.delete(`/knowledge/${k.id}`); load(); };
  const toggleKb = async (k) => {
    try { await api.put(`/knowledge/${k.id}`, { ...k, activa: !k.activa }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const descartarPend = async (p) => { await api.post(`/knowledge/pendientes/${p.id}/descartar`); loadKbPend(); };
  const extraerTexto = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/knowledge/extraer", { texto: bulkTexto });
      setBulkItems(data.entradas.map((e) => ({ ...e, incluir: true })));
      if (!data.entradas.length) toast.error("No se encontraron preguntas con respuesta en el texto");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo procesar el texto"); }
    finally { setBulkBusy(false); }
  };
  const importarArchivoKb = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBulkBusy(true);
    try {
      const { data } = await api.post("/knowledge/importar-archivo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkItems(data.entradas.map((e) => ({ ...e, incluir: true })));
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo leer el archivo"); }
    finally { setBulkBusy(false); ev.target.value = ""; }
  };
  const guardarLote = async () => {
    const entradas = (bulkItems || []).filter((e) => e.incluir).map(({ incluir, ...e }) => e);
    if (!entradas.length) return toast.error("No hay entradas seleccionadas");
    setBulkBusy(true);
    try {
      const { data } = await api.post("/knowledge/lote", { entradas });
      toast.success(`${data.creadas} respuestas cargadas${data.omitidas ? ` (${data.omitidas} ya existían)` : ""}`);
      setBulkOpen(false); setBulkItems(null); setBulkTexto(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBulkBusy(false); }
  };
  const editarBulk = (i, patch) => setBulkItems(bulkItems.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const kbFiltrada = kb.filter((k) => (kbCat === "todas" || (k.categoria || "General") === kbCat)
    && (!kbQuery.trim() || `${k.pregunta} ${k.respuesta}`.toLowerCase().includes(kbQuery.trim().toLowerCase())));
  const kbSinRespuesta = kbFiltrada.filter((k) => !(k.respuesta || "").trim());
  const kbConRespuesta = kbFiltrada.filter((k) => (k.respuesta || "").trim());
  const kbActivasCount = kb.filter((k) => k.activa && (k.respuesta || "").trim()).length;

  const saveUser = async () => {
    try {
      if (userForm.id) await api.put(`/users/${userForm.id}`, userForm);
      else {
        const { data } = await api.post("/users", userForm);
        // Fix N: sin contraseña escrita, el backend genera una temporal que se muestra una sola vez.
        if (data.password_temporal) setResetResult({ user: { name: data.name, email: data.email }, password: data.password_temporal, nuevo: true });
      }
      toast.success("Usuario guardado"); setUserForm(null); load(); loadActividad(rangoActividad);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleUser = async (u) => {
    try { await api.post(`/users/${u.id}/toggle`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const marca = s?.marca || { nombre_comercial: "Marthitashop", logo_url: null, logo_icono_url: null, color_primario: "", color_acento: "", frase_login: "", subtitulo_login: "" };
  const setMarca = (patch) => setS({ ...s, marca: { ...marca, ...patch } });
  const [uploadingLogo, setUploadingLogo] = useState(null);

  const uploadLogo = async (tipo, file) => {
    if (!file) return;
    setUploadingLogo(tipo);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/marca/logo", fd, { params: { tipo }, headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, marca: data });
      toast.success("Logo actualizado. Los cambios se ven en todas las pantallas al recargar (F5).");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { setUploadingLogo(null); }
  };
  const deleteLogo = async (tipo) => {
    try {
      const { data } = await api.delete("/admin/marca/logo", { params: { tipo } });
      setS({ ...s, marca: data });
      toast.success("Logo eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const restoreMarca = async () => {
    try {
      const { data } = await api.post("/admin/marca/restaurar");
      setS({ ...s, marca: data });
      setRestaurarMarcaOpen(false);
      toast.success("Valores por defecto restaurados. Recargá (F5) para verlo en toda la pantalla.");
    } catch (e) { toast.error("Error"); }
  };

  const setNotifPref = async (patch) => {
    const next = { ...notifPrefs, ...patch };
    setNotifPrefs(next);
    try { await api.put("/notifications/preferencias", patch); }
    catch (e) { toast.error("No se pudo guardar la preferencia"); }
  };
  const setNotifTipo = (tipo, val) => setNotifPref({ tipos: { ...notifPrefs.tipos, [tipo]: val } });
  const setResponsableDefault = async (userId) => {
    try {
      await api.put("/notifications/responsable-default", { user_id: userId === "ninguno" ? null : userId });
      setNotifPrefs({ ...notifPrefs, responsable_default: userId === "ninguno" ? null : userId });
      // Fix N: si no, un "Guardar" en otra pestaña manda el asignacion viejo y pisa este cambio.
      setS((prev) => (prev ? { ...prev, asignacion: { ...(prev.asignacion || {}), responsable_default: userId === "ninguno" ? null : userId } } : prev));
      toast.success("Responsable por defecto actualizado");
    } catch (e) { toast.error("Error"); }
  };
  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await subscribePush(api);
      setPushSubscribed(true);
      setBrowserPerm(Notification.permission);
      toast.success("Avisos del navegador activados");
    } catch (e) { toast.error(e.message || "No se pudo activar"); setBrowserPerm(typeof Notification !== "undefined" ? Notification.permission : "default"); }
    finally { setPushBusy(false); }
  };
  const handleDisablePush = async () => {
    setPushBusy(true);
    try { await unsubscribePush(api); setPushSubscribed(false); toast.success("Avisos desactivados"); }
    catch (e) { toast.error("Error"); }
    finally { setPushBusy(false); }
  };
  const sendTestPush = async () => {
    try { await api.post("/notificaciones/probar"); toast.success("Aviso de prueba enviado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const widget = s?.widget || { activo: true, color: marca.color_primario, mensaje_bienvenida: "", avatar_url: "" };
  const setWidget = (patch) => setS({ ...s, widget: { ...widget, ...patch } });
  const snippet = `<script src="${BACKEND_URL}/api/widget/widget.js" async></script>`;
  const [copied, setCopied] = useState(false);
  const copySnippet = () => { navigator.clipboard.writeText(snippet); setCopied(true); toast.success("Snippet copiado"); setTimeout(() => setCopied(false), 2000); };

  const pagos = s?.pagos || { modo_proveedor: "simulado", vencimiento_horas_default: 24, recordatorio_horas: 4 };
  const setPagos = (patch) => setS({ ...s, pagos: { ...pagos, ...patch } });
  // Fix M (2026-09-22): cuentas para transferencias + validación automática de comprobantes.
  const cuentas = pagos.cuentas_bancarias || [];
  const setCuentas = (cs) => setPagos({ cuentas_bancarias: cs });
  const addCuenta = () => setCuentas([...cuentas, { id: nuevoIdZona(), banco: "", tipo: "Ahorros", numero: "", titular: "", rnc_cedula: "", activa: true }]);
  const updateCuenta = (i, field, val) => setCuentas(cuentas.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const removeCuenta = (i) => setCuentas(cuentas.filter((_, idx) => idx !== i));
  const va = { modo: "automatica", tolerancia: 1, monto_maximo: 15000, dias_maximos: 3, ...(pagos.validacion_automatica || {}) };
  const setVa = (patch) => setPagos({ validacion_automatica: { ...va, ...patch } });
  const cuentasActivas = cuentas.filter((c) => c.activa !== false && (c.numero || "").trim());

  const fiscal = s?.fiscal || { modo_proveedor: "simulado", activo: false, rnc_emisor: "", razon_social_emisor: "", direccion_emisor: "", tasa_itbis: 18, precios_incluyen_itbis: true };
  const setFiscal = (patch) => setS({ ...s, fiscal: { ...fiscal, ...patch } });

  if (!s) return <div className="p-8"><Loader /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Configuración" subtitle="Solo la administradora puede editar esto." />
      <Tabs defaultValue="negocio">
        <TabsList className="rounded-full mb-4 flex-wrap h-auto">
          <TabsTrigger value="marca" className="rounded-full gap-1" data-testid="tab-marca"><Palette className="h-4 w-4" /> Marca</TabsTrigger>
          <TabsTrigger value="negocio" className="rounded-full gap-1" data-testid="tab-negocio"><Building2 className="h-4 w-4" /> Negocio</TabsTrigger>
          <TabsTrigger value="envios" className="rounded-full gap-1" data-testid="tab-envios"><Truck className="h-4 w-4" /> Envíos</TabsTrigger>
          <TabsTrigger value="seguimientos" className="rounded-full gap-1" data-testid="tab-seguimientos-config"><Repeat className="h-4 w-4" /> Seguimientos</TabsTrigger>
          <TabsTrigger value="widget" className="rounded-full gap-1" data-testid="tab-widget"><MessageSquareText className="h-4 w-4" /> Widget web</TabsTrigger>
          <TabsTrigger value="pagos" className="rounded-full gap-1" data-testid="tab-pagos-config"><CreditCard className="h-4 w-4" /> Pagos</TabsTrigger>
          <TabsTrigger value="fiscal" className="rounded-full gap-1" data-testid="tab-fiscal-config"><FileText className="h-4 w-4" /> Facturación DGII</TabsTrigger>
          <TabsTrigger value="agente" className="rounded-full gap-1" data-testid="tab-agente"><Bot className="h-4 w-4" /> Agente</TabsTrigger>
          <TabsTrigger value="kb" className="rounded-full gap-1" data-testid="tab-kb"><BookOpen className="h-4 w-4" /> Conocimiento{kbPend.length > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 text-[10px]" data-testid="kb-pendientes-badge">{kbPend.length}</span>}</TabsTrigger>
          <TabsTrigger value="usuarios" className="rounded-full gap-1" data-testid="tab-usuarios"><UsersRound className="h-4 w-4" /> Usuarios</TabsTrigger>
          <TabsTrigger value="notificaciones" className="rounded-full gap-1" data-testid="tab-notificaciones"><Bell className="h-4 w-4" /> Notificaciones</TabsTrigger>
          <TabsTrigger value="auditoria" className="rounded-full gap-1" data-testid="tab-auditoria"><ScrollText className="h-4 w-4" /> Auditoría</TabsTrigger>
        </TabsList>

        <TabsContent value="marca">
          <div className="ms-card p-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="mb-1 block">Logo principal</Label>
                <p className="text-xs text-muted-foreground mb-2">PNG con fondo transparente, mínimo 200 px de ancho. Se usa en el login, en los pedidos impresos y en la página de pago.</p>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 h-20 rounded-xl border border-border bg-white flex items-center justify-center overflow-hidden">
                    {marca.logo_url ? <img src={fileUrl(marca.logo_url)} alt="Logo" className="max-h-16 max-w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 h-20 rounded-xl border border-border flex items-center justify-center overflow-hidden" style={{ backgroundColor: marca.color_primario || "var(--marca-primario)" }}>
                    {marca.logo_url ? <img src={fileUrl(marca.logo_url)} alt="Logo sobre color" className="max-h-16 max-w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-white/70" />}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploadingLogo === "principal"}
                    onChange={(e) => uploadLogo("principal", e.target.files?.[0])} className="rounded-xl" data-testid="marca-logo-principal-input" />
                  {marca.logo_url && <Button size="sm" variant="ghost" className="rounded-full text-red-500 shrink-0" onClick={() => deleteLogo("principal")} data-testid="marca-logo-principal-delete"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
              <div>
                <Label className="mb-1 block">Icono cuadrado</Label>
                <p className="text-xs text-muted-foreground mb-2">Cuadrado, mínimo 256x256 px. Es el que se ve en la pestaña del navegador y en el chat de la web.</p>
                <div className="flex gap-2 mb-2">
                  <div className="h-20 w-20 rounded-xl border border-border bg-white flex items-center justify-center overflow-hidden">
                    {marca.logo_icono_url ? <img src={fileUrl(marca.logo_icono_url)} alt="Icono" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <div className="h-20 w-20 rounded-xl border border-border flex items-center justify-center overflow-hidden" style={{ backgroundColor: marca.color_primario || "var(--marca-primario)" }}>
                    {marca.logo_icono_url ? <img src={fileUrl(marca.logo_icono_url)} alt="Icono sobre color" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-white/70" />}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploadingLogo === "icono"}
                    onChange={(e) => uploadLogo("icono", e.target.files?.[0])} className="rounded-xl" data-testid="marca-logo-icono-input" />
                  {marca.logo_icono_url && <Button size="sm" variant="ghost" className="rounded-full text-red-500 shrink-0" onClick={() => deleteLogo("icono")} data-testid="marca-logo-icono-delete"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="mb-1 block">Color primario</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={marca.color_primario || "#999999"} onChange={(e) => setMarca({ color_primario: e.target.value })} className="rounded-xl h-10 w-16 p-1" data-testid="marca-color-primario" />
                  <Input value={marca.color_primario || ""} onChange={(e) => setMarca({ color_primario: e.target.value })} className="rounded-xl font-mono" />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {SUGERENCIAS_COLOR.map((c) => (
                    <button key={c} type="button" onClick={() => setMarca({ color_primario: c })}
                      className="h-6 w-6 rounded-full border border-border" style={{ backgroundColor: c }} data-testid={`marca-sugerencia-primario-${c}`} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1 block">Color de acento</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={marca.color_acento || "#999999"} onChange={(e) => setMarca({ color_acento: e.target.value })} className="rounded-xl h-10 w-16 p-1" data-testid="marca-color-acento" />
                  <Input value={marca.color_acento || ""} onChange={(e) => setMarca({ color_acento: e.target.value })} className="rounded-xl font-mono" />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {SUGERENCIAS_COLOR.map((c) => (
                    <button key={c} type="button" onClick={() => setMarca({ color_acento: c })}
                      className="h-6 w-6 rounded-full border border-border" style={{ backgroundColor: c }} data-testid={`marca-sugerencia-acento-${c}`} />
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre comercial</Label><Input value={marca.nombre_comercial || ""} onChange={(e) => setMarca({ nombre_comercial: e.target.value })} className="rounded-xl mt-1" data-testid="marca-nombre-comercial" /></div>
            </div>
            <div><Label>Frase del login</Label><Input value={marca.frase_login || ""} onChange={(e) => setMarca({ frase_login: e.target.value })} className="rounded-xl mt-1" data-testid="marca-frase-login" /></div>
            <div><Label>Subtítulo del login</Label><Textarea value={marca.subtitulo_login || ""} onChange={(e) => setMarca({ subtitulo_login: e.target.value })} className="rounded-xl mt-1" data-testid="marca-subtitulo-login" /></div>

            <div className="pt-2 border-t border-border">
              <Label className="mb-2 block">Vista previa en vivo</Label>
              <div className="rounded-2xl border border-border overflow-hidden grid grid-cols-1 sm:grid-cols-2">
                <div className="p-4 text-white" style={{ backgroundColor: marca.color_primario || "var(--marca-primario)" }}>
                  <p className="text-xs opacity-80 mb-3">{marca.nombre_comercial || "Marthitashop"}</p>
                  <p className="font-semibold text-sm mb-1">{marca.frase_login || "Tu tienda de belleza, atendida 24/7."}</p>
                  <p className="text-xs opacity-80">{marca.subtitulo_login}</p>
                </div>
                <div className="p-4 bg-white space-y-3">
                  <button type="button" className="w-full rounded-full py-2 text-sm font-medium" style={{ backgroundColor: marca.color_primario || "#999999", color: (() => {
                    const hex = (marca.color_primario || "#999999").replace("#", "");
                    const r = parseInt(hex.substring(0, 2), 16) || 0, g = parseInt(hex.substring(2, 4), 16) || 0, b = parseInt(hex.substring(4, 6), 16) || 0;
                    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
                    return lum > 0.55 ? "#111111" : "#FFFFFF";
                  })() }} data-testid="marca-preview-boton">Botón primario</button>
                  <span className="inline-block text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: `${marca.color_acento || "#999999"}22`, color: marca.color_acento || "#999999" }}>Badge de ejemplo</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-marca">Guardar cambios</Button>
              <Button variant="outline" className="rounded-full gap-2" onClick={() => setRestaurarMarcaOpen(true)} data-testid="marca-restaurar-btn"><RotateCcw className="h-4 w-4" /> Restaurar valores por defecto</Button>
            </div>
            <p className="text-xs text-muted-foreground">Los cambios se ven en todas las pantallas al recargar (F5).</p>
          </div>
        </TabsContent>

        <TabsContent value="negocio">
          {checklist && (
            <div className="ms-card p-5 mb-4" data-testid="onboarding-checklist-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-semibold text-sm flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Checklist de puesta en marcha</h3>
                <Badge className="rounded-full border-0 bg-accent">{checklist.listos}/{checklist.total} listos</Badge>
              </div>
              <div className="space-y-1.5">
                {checklist.items.map((it) => (
                  <button key={it.id} onClick={() => nav(it.link)} data-testid={`checklist-item-${it.id}`}
                    className="w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left hover:bg-accent transition-colors">
                    <span className="flex items-center gap-2 text-sm min-w-0">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${it.cumplido ? "bg-emerald-500" : "bg-amber-400"}`} />
                      <span className={`truncate ${it.cumplido ? "text-muted-foreground" : ""}`}>{it.titulo}</span>
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="ms-card p-5 mb-4 flex items-center justify-between gap-3" data-testid="demo-seed-card">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Datos de demostración</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {demoState?.demo_seed_enabled
                    ? "Activados: si la base está vacía, el sistema se siembra con datos de ejemplo."
                    : "Desactivados: el sistema no va a volver a sembrar datos de ejemplo automáticamente."}
                </p>
              </div>
            </div>
            <Switch checked={!!demoState?.demo_seed_enabled} onCheckedChange={(v) => (v ? setActivarDemoOpen(true) : desactivarDemo())} data-testid="demo-seed-toggle" />
          </div>
          <div className="ms-card p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre del negocio</Label><Input value={s.nombre_negocio || ""} onChange={(e) => setS({ ...s, nombre_negocio: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Teléfono</Label><Input value={s.telefono || ""} onChange={(e) => setS({ ...s, telefono: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div><Label>Notas adicionales de envío (texto libre)</Label><Textarea value={typeof s.zonas_envio === "string" ? s.zonas_envio : ""} onChange={(e) => setS({ ...s, zonas_envio: e.target.value })} className="rounded-xl mt-1" data-testid="zonas-envio-notas" /></div>
            <div>
              <Label className="mb-2 block">Horarios de atención humana</Label>
              <div className="space-y-2">
                {DIAS.map((d) => (
                  <div key={d} className="flex items-center gap-2">
                    <span className="w-24 text-sm capitalize">{d}</span>
                    <Switch checked={s.horarios?.[d]?.abierto || false} onCheckedChange={(v) => setHorario(d, "abierto", v)} />
                    <Input type="time" value={s.horarios?.[d]?.desde || "09:00"} onChange={(e) => setHorario(d, "desde", e.target.value)} className="rounded-xl w-28" disabled={!s.horarios?.[d]?.abierto} />
                    <Input type="time" value={s.horarios?.[d]?.hasta || "18:00"} onChange={(e) => setHorario(d, "hasta", e.target.value)} className="rounded-xl w-28" disabled={!s.horarios?.[d]?.abierto} />
                  </div>
                ))}
              </div>
            </div>
            <div><Label>Mensaje fuera de horario</Label><Textarea value={s.mensaje_fuera_de_horario || ""} onChange={(e) => setS({ ...s, mensaje_fuera_de_horario: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Regla de expectativa de entrega fuera de horario</Label><Textarea value={s.regla_entrega_fuera_horario || ""} onChange={(e) => setS({ ...s, regla_entrega_fuera_horario: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Umbral de stock bajo por defecto</Label><Input type="number" value={s.umbral_stock_bajo || 5} onChange={(e) => setS({ ...s, umbral_stock_bajo: Number(e.target.value) })} className="rounded-xl mt-1 w-32" /></div>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-settings">Guardar cambios</Button>
          </div>
        </TabsContent>

        <TabsContent value="envios">
          <div className="ms-card p-5 space-y-4">
            <div className="rounded-xl bg-accent/50 px-3 py-2 text-xs text-muted-foreground space-y-1" data-testid="envios-como-se-calcula">
              <p className="font-semibold text-foreground text-sm">Cómo se calcula el envío</p>
              <p>1. El lugar que dice el cliente se ubica en una zona: por el nombre de la zona o por uno de sus sectores.</p>
              <p>2. Se cobra la tarifa de esa zona. Si la compra (productos menos descuento) llega al monto de "envío gratis desde", el envío es 0.</p>
              <p>3. Si paga contra entrega, se suma el recargo de la zona. Si la zona no acepta contra entrega, el agente ofrece transferencia o link de pago.</p>
              <p>4. Si el lugar no está en ninguna zona, el agente pregunta la ciudad o provincia. Nunca inventa el costo.</p>
            </div>
            {zonas.map((z, i) => (
              <div key={zonaKey(z, i)} className={`rounded-2xl border border-border p-4 space-y-3 ${z.activa === false ? "opacity-60" : ""}`} data-testid={`zona-row-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={z.zona} onChange={(e) => updateZona(i, "zona", e.target.value)} placeholder="Nombre de la zona (ej: Distrito Nacional)" className="rounded-xl flex-1 font-medium" data-testid={`zona-nombre-${i}`} />
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-muted-foreground">Activa</span>
                    <Switch checked={z.activa !== false} onCheckedChange={(v) => updateZona(i, "activa", v)} data-testid={`zona-activa-${i}`} />
                  </div>
                  <Button size="sm" variant="ghost" className="rounded-full text-red-500 shrink-0" onClick={() => removeZona(i)} data-testid={`zona-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Tarifa (RD$)</Label>
                    <Input type="number" min={0} value={z.tarifa ?? 0} onChange={(e) => updateZona(i, "tarifa", Number(e.target.value))} className="rounded-xl mt-1" data-testid={`zona-tarifa-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Envío gratis desde (RD$)</Label>
                    <Input type="number" min={0} value={z.gratis_desde ?? ""} onChange={(e) => updateZona(i, "gratis_desde", e.target.value === "" ? null : Number(e.target.value))} placeholder="Sin envío gratis" className="rounded-xl mt-1" data-testid={`zona-gratis-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de entrega</Label>
                    <Select value={z.tipo_entrega || "mensajero"} onValueChange={(v) => updateZona(i, "tipo_entrega", v)}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid={`zona-tipo-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensajero">Mensajero propio</SelectItem>
                        <SelectItem value="paqueteria">Paquetería / agencia</SelectItem>
                        <SelectItem value="retiro">Retiro en tienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Sectores, barrios o ciudades de esta zona</Label>
                  <Textarea rows={2} value={sectoresTexto[zonaKey(z, i)] ?? (z.sectores || []).join(", ")}
                    onChange={(e) => setSectoresTexto({ ...sectoresTexto, [zonaKey(z, i)]: e.target.value })}
                    onBlur={() => commitSectores(i, z)} placeholder="Gazcue, Naco, Piantini, Bella Vista…" className="rounded-xl mt-1" data-testid={`zona-sectores-${i}`} />
                  <p className="text-[11px] text-muted-foreground mt-1">Separados por coma. Si el cliente nombra uno de estos lugares, se le cobra esta zona.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Tiempo de entrega</Label>
                    <Input value={z.tiempo_entrega || ""} onChange={(e) => updateZona(i, "tiempo_entrega", e.target.value)} placeholder="Ej: 24 a 48 horas" className="rounded-xl mt-1" data-testid={`zona-tiempo-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Recargo contra entrega (RD$)</Label>
                    <Input type="number" min={0} value={z.recargo_contra_entrega ?? 0} onChange={(e) => updateZona(i, "recargo_contra_entrega", Number(e.target.value))} className="rounded-xl mt-1" data-testid={`zona-recargo-${i}`} />
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2 sm:mt-5">
                    <span className="text-xs">Acepta contra entrega</span>
                    <Switch checked={z.permite_contra_entrega !== false} onCheckedChange={(v) => updateZona(i, "permite_contra_entrega", v)} data-testid={`zona-ce-${i}`} />
                  </div>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-full gap-2" onClick={() => addZona()} data-testid="zona-add"><Plus className="h-4 w-4" /> Agregar zona</Button>
              {!zonas.some((z) => z.tipo_entrega === "retiro") && (
                <Button variant="outline" className="rounded-full gap-2" data-testid="zona-add-retiro"
                  onClick={() => addZona({ zona: "Retiro en tienda", tarifa: 0, tipo_entrega: "retiro", sectores: ["retiro en tienda", "paso a buscar", "recoger"] })}>
                  <Plus className="h-4 w-4" /> Agregar retiro en tienda
                </Button>
              )}
            </div>
            <div><Button onClick={guardarZonas} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-envios">Guardar zonas</Button></div>
          </div>
          <div className="ms-card p-5 space-y-3 mt-4" data-testid="envio-probador">
            <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Probar el cálculo</h3>
            <p className="text-xs text-muted-foreground">Escribir un lugar como lo diría un cliente (un sector, una ciudad o una dirección). Usa las zonas de esta pantalla, aunque todavía no estén guardadas.</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <Input value={pruebaEnvio.lugar} onChange={(e) => setPruebaEnvio({ ...pruebaEnvio, lugar: e.target.value })} placeholder="Ej: Gazcue" className="rounded-xl sm:col-span-2" data-testid="envio-prueba-lugar" />
              <Input type="number" min={0} value={pruebaEnvio.subtotal} onChange={(e) => setPruebaEnvio({ ...pruebaEnvio, subtotal: e.target.value })} placeholder="Compra RD$" className="rounded-xl" data-testid="envio-prueba-subtotal" />
              <Select value={pruebaEnvio.metodo_pago} onValueChange={(v) => setPruebaEnvio({ ...pruebaEnvio, metodo_pago: v })}>
                <SelectTrigger className="rounded-xl" data-testid="envio-prueba-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo_contra_entrega">Contra entrega</SelectItem>
                  <SelectItem value="link_pago">Link de pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="rounded-full" onClick={probarEnvio} disabled={!pruebaEnvio.lugar.trim()} data-testid="envio-prueba-btn">Calcular</Button>
            {pruebaEnvioRes && (pruebaEnvioRes.ok ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="envio-prueba-resultado">
                <p className="font-semibold">Zona {pruebaEnvioRes.zona} · Envío RD$ {Number(pruebaEnvioRes.costo_envio).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{pruebaEnvioRes.detalle}</p>
                {pruebaEnvioRes.tiempo_entrega && <p className="text-xs text-muted-foreground">Entrega: {pruebaEnvioRes.tiempo_entrega}</p>}
                {pruebaEnvioRes.coincidencia === "aproximada" && <p className="text-xs text-amber-700 mt-1">Se ubicó por parecido (posible error de tipeo). Conviene agregar ese lugar como sector.</p>}
                {!pruebaEnvioRes.permite_contra_entrega && <p className="text-xs text-amber-700 mt-1">Esta zona no acepta contra entrega.</p>}
              </div>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm" data-testid="envio-prueba-resultado">
                {pruebaEnvioRes.sin_zonas ? <p className="font-semibold">No hay zonas activas: el agente no informa un costo de envío y deriva la consulta.</p> : (
                  <>
                    <p className="font-semibold">{pruebaEnvioRes.motivo === "ambigua" ? `«${pruebaEnvioRes.lugar}» coincide con más de una zona: ${pruebaEnvioRes.candidatas.join(", ")}` : `«${pruebaEnvioRes.lugar}» no está en ninguna zona`}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">El agente le preguntaría al cliente la ciudad o provincia. Para que se cobre solo, agregar ese lugar como sector de la zona que corresponda.</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="seguimientos">
          <div className="ms-card p-5 space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm font-medium">Activar seguimientos automáticos</span>
              <Switch checked={!!seg.activo} onCheckedChange={(v) => setSeg({ activo: v })} data-testid="seguimiento-activo" />
            </div>
            <div>
              <Label>Cantidad de seguimientos</Label>
              <Select value={String(seg.cantidad || 1)} onValueChange={(v) => setSeg({ cantidad: Number(v) })}>
                <SelectTrigger className="rounded-xl mt-1 w-40" data-testid="seguimiento-cantidad"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1 seguimiento</SelectItem><SelectItem value="2">2 seguimientos</SelectItem></SelectContent>
              </Select>
            </div>
            {[0, 1].slice(0, seg.cantidad || 1).map((idx) => {
              const { h, m } = minutesToHM((seg.esperas_minutos || [240, 1440])[idx]);
              return (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Espera antes del seguimiento #{idx + 1}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="number" min={0} value={h} onChange={(e) => setEsperaHM(idx, "h", e.target.value)} className="rounded-xl w-20" data-testid={`espera-horas-${idx}`} />
                      <span className="text-xs text-muted-foreground">horas</span>
                      <Input type="number" min={0} max={59} value={m} onChange={(e) => setEsperaHM(idx, "m", e.target.value)} className="rounded-xl w-20" data-testid={`espera-minutos-${idx}`} />
                      <span className="text-xs text-muted-foreground">min</span>
                    </div>
                  </div>
                  <div>
                    <Label>Plantilla #{idx + 1} ({"{nombre}"}, {"{producto}"})</Label>
                    <Textarea value={(seg.plantillas || [])[idx] || ""} onChange={(e) => setPlantilla(idx, e.target.value)} className="rounded-xl mt-1" data-testid={`plantilla-${idx}`} />
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm font-medium">Respetar horario de atención</span>
              <Switch checked={!!seg.respetar_horario} onCheckedChange={(v) => setSeg({ respetar_horario: v })} data-testid="seguimiento-horario" />
            </div>
            <div>
              <Label>Plantilla de WhatsApp para cuando la ventana de 24h esté cerrada</Label>
              <Select value={seg.plantilla_whatsapp_id || "none"} onValueChange={(v) => setSeg({ plantilla_whatsapp_id: v === "none" ? null : v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="seguimiento-plantilla"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin plantilla (quedará "requiere plantilla")</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre_meta}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Las plantillas se registran en Canales → Plantillas.</p>
            </div>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-seguimientos">Guardar seguimientos</Button>
          </div>
        </TabsContent>

        <TabsContent value="widget">
          <div className="ms-card p-5 space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm font-medium">Activar widget en el sitio</span>
              <Switch checked={widget.activo !== false} onCheckedChange={(v) => setWidget({ activo: v })} data-testid="widget-activo" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Color principal</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="color" value={widget.color || marca.color_primario} onChange={(e) => setWidget({ color: e.target.value })} className="rounded-xl h-10 w-16 p-1" data-testid="widget-color" />
                  <Input value={widget.color || marca.color_primario} onChange={(e) => setWidget({ color: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Avatar (URL de imagen, opcional)</Label>
                <Input value={widget.avatar_url || ""} onChange={(e) => setWidget({ avatar_url: e.target.value })} className="rounded-xl mt-1" placeholder="https://…" data-testid="widget-avatar" />
              </div>
            </div>
            <div><Label>Mensaje de bienvenida</Label><Textarea value={widget.mensaje_bienvenida || ""} onChange={(e) => setWidget({ mensaje_bienvenida: e.target.value })} className="rounded-xl mt-1" data-testid="widget-mensaje" /></div>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-widget">Guardar cambios</Button>

            <div className="pt-4 border-t border-border">
              <Label className="mb-2 block">Snippet de instalación</Label>
              <p className="text-xs text-muted-foreground mb-2">Pegá esto en el theme de tu tienda (justo antes de <span className="font-mono">&lt;/body&gt;</span>). No necesita ningún <span className="font-mono">&lt;div&gt;</span> adicional.</p>
              <div className="relative">
                <pre className="rounded-xl bg-muted p-3 text-xs overflow-x-auto" data-testid="widget-snippet">{snippet}</pre>
                <Button size="sm" variant="outline" className="absolute top-2 right-2 rounded-full gap-1 h-7" onClick={copySnippet} data-testid="widget-copy-snippet">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copiado" : "Copiar"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Probalo sin tocar tu tienda en <a href="/chat-demo" target="_blank" rel="noreferrer" className="text-primary underline">/chat-demo</a>.</p>
            </div>

            <div className="pt-4 border-t border-border">
              <Label className="mb-2 block">Vista previa</Label>
              <div className="rounded-2xl border border-border overflow-hidden" style={{ height: 420 }}>
                <iframe src="/chat-demo" title="Vista previa del widget" className="w-full h-full border-0" data-testid="widget-preview-iframe" />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pagos">
          <div className="ms-card p-5 space-y-4">
            <div>
              <Label>Proveedor de la pasarela</Label>
              <Select value={pagos.modo_proveedor} onValueChange={(v) => setPagos({ modo_proveedor: v })}>
                <SelectTrigger className="rounded-xl mt-1 w-56" data-testid="pagos-modo-proveedor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simulado">Simulado (demo)</SelectItem>
                  <SelectItem value="real">Real (variables de entorno)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">"Real" requiere PAYMENT_PROVIDER_API_URL / PAYMENT_PROVIDER_API_KEY en el .env del backend. Sin esas credenciales, los links quedan bloqueados.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Vencimiento por defecto (horas)</Label><Input type="number" min={1} value={pagos.vencimiento_horas_default} onChange={(e) => setPagos({ vencimiento_horas_default: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="pagos-vencimiento" /></div>
              <div><Label>Recordatorio si sigue pendiente (horas)</Label><Input type="number" min={1} value={pagos.recordatorio_horas} onChange={(e) => setPagos({ recordatorio_horas: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="pagos-recordatorio" /></div>
            </div>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-pagos">Guardar</Button>
          </div>

          <div className="ms-card p-5 space-y-3 mt-4" data-testid="cuentas-bancarias-card">
            <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Cuentas para transferencias</h3>
            <p className="text-xs text-muted-foreground">El agente se las pasa al cliente que elige transferencia, y la validación automática comprueba que el comprobante sea a una de estas cuentas.</p>
            {cuentas.map((c, i) => (
              <div key={c.id || i} className="rounded-2xl border border-border p-3 space-y-2" data-testid={`cuenta-row-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <div className="sm:col-span-2"><Label className="text-xs">Banco</Label><Input value={c.banco || ""} onChange={(e) => updateCuenta(i, "banco", e.target.value)} placeholder="Banco Popular" className="rounded-xl mt-1" data-testid={`cuenta-banco-${i}`} /></div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={c.tipo || "Ahorros"} onValueChange={(v) => updateCuenta(i, "tipo", v)}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid={`cuenta-tipo-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Ahorros">Ahorros</SelectItem><SelectItem value="Corriente">Corriente</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label className="text-xs">Número de cuenta</Label><Input value={c.numero || ""} onChange={(e) => updateCuenta(i, "numero", e.target.value)} className="rounded-xl mt-1 font-mono" data-testid={`cuenta-numero-${i}`} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                  <div className="sm:col-span-2"><Label className="text-xs">Titular</Label><Input value={c.titular || ""} onChange={(e) => updateCuenta(i, "titular", e.target.value)} className="rounded-xl mt-1" data-testid={`cuenta-titular-${i}`} /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">RNC / Cédula del titular</Label><Input value={c.rnc_cedula || ""} onChange={(e) => updateCuenta(i, "rnc_cedula", e.target.value)} className="rounded-xl mt-1" data-testid={`cuenta-rnc-${i}`} /></div>
                  <div className="flex items-center justify-end gap-2 pb-1">
                    <Switch checked={c.activa !== false} onCheckedChange={(v) => updateCuenta(i, "activa", v)} data-testid={`cuenta-activa-${i}`} />
                    <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => removeCuenta(i)} data-testid={`cuenta-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-full gap-2" onClick={addCuenta} data-testid="cuenta-add"><Plus className="h-4 w-4" /> Agregar cuenta</Button>
              <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-cuentas">Guardar cuentas</Button>
            </div>
          </div>

          <div className="ms-card p-5 space-y-3 mt-4" data-testid="validacion-automatica-card">
            <h3 className="font-head font-semibold text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Validación automática de comprobantes</h3>
            <p className="text-xs text-muted-foreground">
              Cuando un cliente con un pedido pendiente manda la foto de su transferencia por el chat, se lee el comprobante y se hacen 8 controles:
              que sea un comprobante, el monto, la moneda, la cuenta de destino, la fecha, que la referencia y la imagen no se hayan usado antes, y el monto máximo.
              Si todos pasan, el pago queda validado y el pedido pasa a pagado. Si alguno falla, va a la cola de Pagos con el motivo. Nunca rechaza un pago sola.
            </p>
            <div>
              <Label>Modo</Label>
              <Select value={va.modo} onValueChange={(v) => setVa({ modo: v })}>
                <SelectTrigger className="rounded-xl mt-1 sm:w-96" data-testid="validacion-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="automatica">Automática: valida sola si todo coincide</SelectItem>
                  <SelectItem value="asistida">Asistida: lee y controla, aprueba una persona</SelectItem>
                  <SelectItem value="manual">Manual: sin lectura automática</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Diferencia de monto aceptada (RD$)</Label><Input type="number" min={0} value={va.tolerancia} onChange={(e) => setVa({ tolerancia: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="validacion-tolerancia" /></div>
              <div><Label className="text-xs">Monto máximo para validar sin revisión (RD$)</Label><Input type="number" min={0} value={va.monto_maximo} onChange={(e) => setVa({ monto_maximo: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="validacion-monto-maximo" /></div>
              <div><Label className="text-xs">Antigüedad máxima del comprobante (días)</Label><Input type="number" min={0} value={va.dias_maximos} onChange={(e) => setVa({ dias_maximos: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="validacion-dias" /></div>
            </div>
            {va.modo === "automatica" && cuentasActivas.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2" data-testid="validacion-sin-cuentas">Sin cuentas cargadas no se puede comprobar el destino: ningún pago se va a validar solo hasta cargar al menos una.</p>
            )}
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-validacion">Guardar</Button>
          </div>
        </TabsContent>

        <TabsContent value="fiscal">
          <div className="ms-card p-5 space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm font-medium">Emisión automática al pasar un pedido a pagado</span>
              <Switch checked={!!fiscal.activo} onCheckedChange={(v) => setFiscal({ activo: v })} data-testid="fiscal-activo" />
            </div>
            <div>
              <Label>Proveedor fiscal</Label>
              <Select value={fiscal.modo_proveedor} onValueChange={(v) => setFiscal({ modo_proveedor: v })}>
                <SelectTrigger className="rounded-xl mt-1 w-56" data-testid="fiscal-modo-proveedor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simulado">Simulado (demo, NO válido ante DGII)</SelectItem>
                  <SelectItem value="real">Real (proveedor certificado)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">"Real" requiere FISCAL_PROVIDER_API_URL / FISCAL_PROVIDER_API_KEY en el .env del backend.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>RNC emisor</Label><Input value={fiscal.rnc_emisor} onChange={(e) => setFiscal({ rnc_emisor: e.target.value })} className="rounded-xl mt-1" data-testid="fiscal-rnc" /></div>
              <div><Label>Razón social emisor</Label><Input value={fiscal.razon_social_emisor} onChange={(e) => setFiscal({ razon_social_emisor: e.target.value })} className="rounded-xl mt-1" data-testid="fiscal-razon-social" /></div>
            </div>
            <div><Label>Dirección emisor</Label><Input value={fiscal.direccion_emisor} onChange={(e) => setFiscal({ direccion_emisor: e.target.value })} className="rounded-xl mt-1" data-testid="fiscal-direccion" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Tasa de ITBIS (%)</Label><Input type="number" value={fiscal.tasa_itbis} onChange={(e) => setFiscal({ tasa_itbis: Number(e.target.value) })} className="rounded-xl mt-1" data-testid="fiscal-tasa-itbis" /></div>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2 mt-6">
                <span className="text-sm">Precios incluyen ITBIS</span>
                <Switch checked={!!fiscal.precios_incluyen_itbis} onCheckedChange={(v) => setFiscal({ precios_incluyen_itbis: v })} data-testid="fiscal-precios-incluyen-itbis" />
              </div>
            </div>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-fiscal">Guardar</Button>
          </div>
        </TabsContent>

        <TabsContent value="agente">
          <div className="ms-card p-5 space-y-4">
            <div><Label>Tono del agente</Label><Textarea rows={5} value={s.tono_del_agente || ""} onChange={(e) => setS({ ...s, tono_del_agente: e.target.value })} className="rounded-xl mt-1" data-testid="tono-agente" /></div>
            <p className="text-xs text-muted-foreground">Este texto se inyecta en el system prompt del agente junto con las reglas, promos y base de conocimiento.</p>
            <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-tono">Guardar tono</Button>
          </div>
        </TabsContent>

        <TabsContent value="kb">
          <div className="ms-card p-4 mb-3 text-sm text-muted-foreground" data-testid="kb-explicacion">
            El agente responde las preguntas del negocio SOLO con estas respuestas (hoy usa <strong className="text-foreground">{kbActivasCount}</strong>).
            Cuando un cliente pregunta algo que no está acá, el agente no inventa: la pregunta aparece en "Por responder".
          </div>

          {kbPend.length > 0 && (
            <div className="ms-card p-4 mb-3 space-y-2" data-testid="kb-pendientes">
              <p className="font-head font-semibold text-sm flex items-center gap-2"><MessageCircleQuestion className="h-4 w-4 text-primary" /> Por responder ({kbPend.length})</p>
              {kbPend.map((p) => (
                <div key={p.id} className="rounded-xl border border-border px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`kb-pendiente-${p.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{p.pregunta}</p>
                    <p className="text-xs text-muted-foreground">{p.veces} {p.veces === 1 ? "vez" : "veces"} · última {dt(p.ultima_vez)}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid={`kb-responder-${p.id}`}
                      onClick={() => setKbForm({ pregunta: p.pregunta, respuesta: "", activa: true, categoria: "General", pendiente_id: p.id })}>Responder</Button>
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => descartarPend(p)} data-testid={`kb-descartar-${p.id}`}>Descartar</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Input value={kbQuery} onChange={(e) => setKbQuery(e.target.value)} placeholder="Buscar pregunta o respuesta…" className="rounded-xl flex-1 min-w-[200px]" data-testid="kb-buscar" />
            <Select value={kbCat} onValueChange={setKbCat}>
              <SelectTrigger className="rounded-xl w-52" data-testid="kb-filtro-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {KB_CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setBulkOpen(true); setBulkItems(null); }} className="rounded-full gap-2" data-testid="kb-carga-masiva"><Upload className="h-4 w-4" /> Cargar varias</Button>
            <Button onClick={() => setKbForm({ pregunta: "", respuesta: "", activa: true, categoria: "General" })} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-kb"><Plus className="h-4 w-4" /> Nueva entrada</Button>
          </div>

          {kbSinRespuesta.length > 0 && (
            <div className="ms-card p-4 mb-3 space-y-2" data-testid="kb-sugeridas">
              <p className="font-head font-semibold text-sm">Para completar ({kbSinRespuesta.length})</p>
              <p className="text-xs text-muted-foreground">Preguntas que los clientes suelen hacer. Sin respuesta, el agente no las usa.</p>
              {kbSinRespuesta.map((k) => (
                <div key={k.id} className="rounded-xl border border-dashed border-border px-3 py-2 flex items-center justify-between gap-2" data-testid={`kb-sugerida-${k.id}`}>
                  <span className="text-sm min-w-0">{k.pregunta} <span className="text-xs text-muted-foreground">· {k.categoria || "General"}</span></span>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setKbForm({ ...k, categoria: k.categoria || "General", activa: true })} data-testid={`kb-completar-${k.id}`}>Completar</Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => delKb(k)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {kbConRespuesta.map((k) => (
              <div key={k.id} className="ms-card p-4" data-testid={`kb-${k.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{k.pregunta}</p>
                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{k.respuesta}</p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <Badge className="rounded-full border-0 bg-muted text-muted-foreground">{k.categoria || "General"}</Badge>
                      <Badge className="rounded-full border-0 bg-accent text-accent-foreground">{KB_ORIGEN[k.origen] || k.origen}</Badge>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Switch checked={k.activa} onCheckedChange={() => toggleKb(k)} />
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setKbForm({ ...k, categoria: k.categoria || "General" })}>Editar</Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => delKb(k)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {kbConRespuesta.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No hay respuestas con esos filtros.</p>}
          </div>
        </TabsContent>

        <TabsContent value="usuarios">
          <div className="ms-card p-4 mb-3">
            <p className="text-sm text-muted-foreground">Una cuenta por persona: así queda registrado quién respondió cada mensaje, quién tomó cada conversación, quién validó cada pago y qué ventas cerró cada una. No compartir cuentas.</p>
            <p className="text-sm text-muted-foreground">Al crear un usuario sin escribir contraseña se genera una temporal y un mensaje listo para enviarle. Cada persona cambia su contraseña desde su nombre, abajo a la izquierda.</p>
          </div>

          {equipoPrefs && (
            <div className="ms-card p-4 mb-3 space-y-2" data-testid="equipo-preferencias">
              <p className="font-head font-semibold text-sm">Cómo trabaja el equipo</p>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2 gap-3">
                <div>
                  <p className="text-sm">Firmar los mensajes con el nombre de quien responde</p>
                  <p className="text-xs text-muted-foreground">El cliente ve, por ejemplo, "— Ana" al final de cada mensaje del equipo.</p>
                </div>
                <Switch checked={!!equipoPrefs.firmar_mensajes} onCheckedChange={(v) => setEquipoPref({ firmar_mensajes: v })} data-testid="equipo-firmar-mensajes" />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2 gap-3 flex-wrap">
                <div>
                  <p className="text-sm">Qué conversaciones ve cada operador</p>
                  <p className="text-xs text-muted-foreground">La dueña y la contadora siempre ven todas.</p>
                </div>
                <Select value={equipoPrefs.operadores_ven || "todas"} onValueChange={(v) => setEquipoPref({ operadores_ven: v })}>
                  <SelectTrigger className="rounded-xl w-64" data-testid="equipo-operadores-ven"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas las conversaciones</SelectItem>
                    <SelectItem value="propias">Solo las suyas y las sin asignar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="ms-card p-4 mb-3" data-testid="equipo-actividad">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <p className="font-head font-semibold text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Actividad del equipo</p>
              <div className="flex gap-1">
                {[[1, "Hoy"], [7, "7 días"], [30, "30 días"]].map(([d, label]) => (
                  <button key={d} onClick={() => setRangoActividad(d)} data-testid={`actividad-rango-${d}`}
                    className={`text-xs rounded-full px-3 py-1 ${rangoActividad === d ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{label}</button>
                ))}
              </div>
            </div>
            {!actividad ? <Loader /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-3">Persona</th><th className="pr-3">Mensajes</th><th className="pr-3">Conversaciones</th>
                      <th className="pr-3">Respuesta (mediana)</th><th className="pr-3">Pagos validados</th><th className="pr-3">Ventas</th><th>Abiertas ahora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actividad.usuarios.map((u) => (
                      <tr key={u.id} className="border-t border-border" data-testid={`actividad-${u.id}`}>
                        <td className="py-1.5 pr-3"><span className="font-medium">{u.nombre}</span> <span className="text-muted-foreground capitalize">· {u.rol}</span>{!u.activo && <span className="text-muted-foreground"> · inactivo</span>}</td>
                        <td className="pr-3">{u.mensajes}</td>
                        <td className="pr-3">{u.conversaciones_atendidas}</td>
                        <td className="pr-3">{u.tiempo_respuesta_min == null ? "—" : u.tiempo_respuesta_min < 60 ? `${u.tiempo_respuesta_min} min` : `${(u.tiempo_respuesta_min / 60).toFixed(1)} h`}</td>
                        <td className="pr-3">{u.pagos_validados}</td>
                        <td className="pr-3">{u.ventas} · RD$ {Number(u.ventas_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                        <td>{u.conversaciones_abiertas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end mb-3"><Button onClick={() => setUserForm({ nombre: "", email: "", password: "", role: "operador", activo: true })} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-user"><Plus className="h-4 w-4" /> Nuevo usuario</Button></div>
          <TooltipProvider>
          <div className="space-y-2">
            {users.map((u) => {
              const esUltimoAdmin = u.role === "admin" && u.activo && users.filter((x) => x.role === "admin" && x.activo).length <= 1;
              const esUnoMismo = u.id === currentUser?.id;
              const bloqueado = u.activo && (esUltimoAdmin || esUnoMismo);
              return (
              <div key={u.id} className="ms-card p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`user-${u.id}`}>
                <div>
                  <p className="font-semibold text-sm">{u.name} <span className="text-xs text-muted-foreground capitalize">· {u.role}</span></p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Último ingreso: {u.ultimo_login ? dt(u.ultimo_login) : "Nunca"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full border-0 ${u.activo ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>{u.activo ? "Activo" : "Inactivo"}</Badge>
                  {u.must_change_password && <Badge className="rounded-full border-0 bg-amber-50 text-amber-600">Debe cambiarla</Badge>}
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => resetPassword(u)} data-testid={`reset-password-${u.id}`}><KeyRound className="h-3.5 w-3.5" /> Restablecer contraseña</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setUserForm({ nombre: u.name, email: u.email, role: u.role, activo: u.activo, id: u.id, password: "" })}>Editar</Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Switch checked={u.activo} disabled={bloqueado} onCheckedChange={() => toggleUser(u)} data-testid={`toggle-acceso-${u.id}`} />
                      </span>
                    </TooltipTrigger>
                    {bloqueado && (
                      <TooltipContent>
                        {esUnoMismo ? "No podés desactivar tu propia cuenta" : "No podés desactivar al último administrador activo"}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
              );
            })}
          </div>
          </TooltipProvider>
        </TabsContent>

        <TabsContent value="notificaciones">
          {!notifPrefs ? <Loader /> : (
            <div className="space-y-4">
              <div className="ms-card p-5 space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                  <span className="text-sm font-medium flex items-center gap-2"><Volume2 className="h-4 w-4 text-primary" /> Sonido al llegar un aviso</span>
                  <Switch checked={notifPrefs.sonido} onCheckedChange={(v) => setNotifPref({ sonido: v })} data-testid="notif-sonido" />
                </div>

                <div className="rounded-xl border border-border px-3 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2"><BellRing className="h-4 w-4 text-primary" /> Avisos del navegador</span>
                    <Badge className="rounded-full border-0 bg-accent capitalize">{browserPerm === "granted" ? "Concedido" : browserPerm === "denied" ? "Bloqueado" : "Sin pedir"}</Badge>
                  </div>
                  {browserPerm !== "granted" && browserPerm !== "denied" && (
                    <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90 mt-2" onClick={handleEnablePush} disabled={pushBusy} data-testid="activar-avisos-btn">Activar avisos</Button>
                  )}
                  {browserPerm === "denied" && (
                    <p className="text-xs text-muted-foreground mt-2">Tu navegador bloqueó los avisos. Para desbloquear: abrí la configuración del sitio (ícono de candado en la barra de direcciones) → Notificaciones → Permitir.</p>
                  )}
                  {browserPerm === "granted" && (
                    <p className="text-xs text-muted-foreground mt-2">Activados en este navegador. {pushSubscribed && <button className="text-primary underline" onClick={handleDisablePush} data-testid="desactivar-avisos-btn">Desactivar</button>}</p>
                  )}
                </div>

                <div className="rounded-xl border border-border px-3 py-3">
                  <span className="text-sm font-medium flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Avisos en el celular</span>
                  {!notifPrefs.push_configurado ? (
                    <p className="text-xs text-muted-foreground mt-2">Los avisos al celular todavía no están configurados.</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mt-2">Para que suene el celular, entrá a la plataforma desde el navegador del teléfono y agregala a la pantalla de inicio. En iPhone hace falta iOS 16.4 o más nuevo.</p>
                      <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={sendTestPush} data-testid="enviar-aviso-prueba-btn">Enviar aviso de prueba</Button>
                    </>
                  )}
                </div>

                <div className="pt-2 border-t border-border">
                  <Label className="mb-2 block">Qué avisar</Label>
                  <div className="space-y-2">
                    {[["derivacion", "Conversación derivada a humano"], ["pago_pendiente", "Pago pendiente de validar"], ["pago_validado", "Pago validado automáticamente"],
                      ["stock_bajo", "Producto agotado o con stock bajo"], ["pedido_nuevo", "Pedido nuevo"]].map(([k, label]) => (
                      <div key={k} className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                        <span className="text-sm">{label}</span>
                        <Switch checked={!!notifPrefs.tipos?.[k]} onCheckedChange={(v) => setNotifTipo(k, v)} data-testid={`notif-tipo-${k}`} />
                      </div>
                    ))}
                  </div>
                </div>

                {notifPrefs.usuarios && (
                  <div className="pt-2 border-t border-border">
                    <Label className="mb-1 block">Responsable por defecto</Label>
                    <p className="text-xs text-muted-foreground mb-2">A quién se le asignan automáticamente las conversaciones que el agente deriva.</p>
                    <Select value={notifPrefs.responsable_default || "ninguno"} onValueChange={setResponsableDefault}>
                      <SelectTrigger className="rounded-xl w-64" data-testid="responsable-default-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ninguno">Sin responsable por defecto</SelectItem>
                        {notifPrefs.usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="auditoria">
          {audit.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Sin acciones registradas todavía.</p> : (
            <div className="space-y-2">
              {audit.map((a) => (
                <div key={a.id} className="ms-card p-3 flex items-center justify-between gap-3" data-testid={`audit-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm"><span className="font-semibold">{a.usuario}</span> · <span className="capitalize">{a.accion.replace(/_/g, " ")}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{a.detalle}</p>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{dt(a.fecha)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* KB dialog */}
      <Dialog open={!!kbForm} onOpenChange={(o) => !o && setKbForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{kbForm?.pendiente_id ? "Responder pregunta de cliente" : kbForm?.id ? "Editar entrada" : "Nueva entrada"}</DialogTitle></DialogHeader>
          {kbForm && (
            <div className="space-y-3">
              <div><Label>Pregunta</Label><Input value={kbForm.pregunta} onChange={(e) => setKbForm({ ...kbForm, pregunta: e.target.value })} className="rounded-xl mt-1" data-testid="kb-pregunta" /></div>
              <div>
                <Label>Categoría</Label>
                <Select value={kbForm.categoria || "General"} onValueChange={(v) => setKbForm({ ...kbForm, categoria: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="kb-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{KB_CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Respuesta</Label><Textarea value={kbForm.respuesta} onChange={(e) => setKbForm({ ...kbForm, respuesta: e.target.value })} className="rounded-xl mt-1" data-testid="kb-respuesta" /></div>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">Activa</span><Switch checked={kbForm.activa} onCheckedChange={(v) => setKbForm({ ...kbForm, activa: v })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setKbForm(null)}>Cancelar</Button><Button onClick={saveKb} className="rounded-full bg-primary hover:bg-primary/90" data-testid="kb-save">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix N (2026-09-22): carga masiva de preguntas frecuentes, siempre con vista previa */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkItems(null); } }}>
        <DialogContent className="w-[min(760px,95vw)] max-w-[min(760px,95vw)] max-h-[90vh] overflow-y-auto" data-testid="kb-carga-masiva-modal">
          <DialogHeader><DialogTitle>Cargar varias preguntas frecuentes</DialogTitle></DialogHeader>
          {!bulkItems ? (
            <div className="space-y-4">
              <div>
                <Label>Pegar un texto</Label>
                <p className="text-xs text-muted-foreground mb-1">Políticas de envío, cambios, horarios, respuestas que ya se usan por WhatsApp… Se separa en preguntas y respuestas usando solo lo que dice el texto.</p>
                <Textarea rows={8} value={bulkTexto} onChange={(e) => setBulkTexto(e.target.value)} className="rounded-xl" data-testid="kb-texto-masivo" />
                <Button onClick={extraerTexto} disabled={bulkBusy || bulkTexto.trim().length < 20} className="rounded-full bg-primary hover:bg-primary/90 mt-2" data-testid="kb-extraer">{bulkBusy ? "Procesando…" : "Separar en preguntas"}</Button>
              </div>
              <div className="border-t border-border pt-3">
                <Label>O subir un Excel / CSV</Label>
                <p className="text-xs text-muted-foreground mb-1">Con columnas Pregunta y Respuesta (Categoría es opcional).</p>
                <Input type="file" accept=".xlsx,.xls,.csv" onChange={importarArchivoKb} disabled={bulkBusy} className="rounded-xl max-w-xs" data-testid="kb-archivo" />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Revisar antes de guardar: se puede corregir cada respuesta o apagar las que no van.</p>
              {bulkItems.map((e, i) => (
                <div key={i} className={`rounded-xl border border-border p-3 space-y-1.5 ${e.incluir ? "" : "opacity-50"}`} data-testid={`kb-lote-${i}`}>
                  <div className="flex items-center gap-2">
                    <Switch checked={e.incluir} onCheckedChange={(v) => editarBulk(i, { incluir: v })} />
                    <Input value={e.pregunta} onChange={(ev) => editarBulk(i, { pregunta: ev.target.value })} className="rounded-xl h-8 text-sm" />
                    <Select value={e.categoria} onValueChange={(v) => editarBulk(i, { categoria: v })}>
                      <SelectTrigger className="rounded-xl h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{KB_CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Textarea rows={2} value={e.respuesta} onChange={(ev) => editarBulk(i, { respuesta: ev.target.value })} className="rounded-xl text-sm" />
                </div>
              ))}
              {bulkItems.length === 0 && <p className="text-sm text-muted-foreground">No se encontraron preguntas.</p>}
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setBulkItems(null)}>Volver</Button>
                <Button onClick={guardarLote} disabled={bulkBusy || !bulkItems.some((e) => e.incluir)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="kb-guardar-lote">{bulkBusy ? "Guardando…" : `Guardar ${bulkItems.filter((e) => e.incluir).length}`}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User dialog */}
      <Dialog open={!!userForm} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{userForm?.id ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          {userForm && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={userForm.nombre} onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })} className="rounded-xl mt-1" data-testid="user-nombre" /></div>
              <div><Label>Correo</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="rounded-xl mt-1" data-testid="user-email" /></div>
              <div><Label>Contraseña {userForm.id ? "(dejar vacío para no cambiar)" : "(dejar vacía para generar una temporal)"}</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className="rounded-xl mt-1" data-testid="user-password" /></div>
              <div>
                <Label>Rol</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="operador">Operador</SelectItem><SelectItem value="contadora">Contadora</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setUserForm(null)}>Cancelar</Button><Button onClick={saveUser} className="rounded-full bg-primary hover:bg-primary/90" data-testid="user-save">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate demo confirm dialog */}
      <Dialog open={activarDemoOpen} onOpenChange={setActivarDemoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reactivar datos de demostración</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Esto vuelve a sembrar productos, clientes, pedidos y conversaciones de ejemplo si la base está vacía. Escribí <strong>ACTIVAR</strong> para confirmar.</p>
          <Input value={activarDemoText} onChange={(e) => setActivarDemoText(e.target.value)} className="rounded-xl" data-testid="activar-demo-confirm-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setActivarDemoOpen(false); setActivarDemoText(""); }}>Cancelar</Button>
            <Button disabled={activarDemoText !== "ACTIVAR"} onClick={activarDemo} className="rounded-full bg-primary hover:bg-primary/90" data-testid="activar-demo-submit">Reactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password result */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> {resetResult?.nuevo ? "Usuario creado" : "Contraseña temporal"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Pasale esta contraseña a <strong>{resetResult?.user?.name}</strong>. Cuando entre, el sistema le va a pedir que la cambie.</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={resetResult?.password || ""} className="rounded-xl font-mono" data-testid="reset-password-value" />
            <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => { navigator.clipboard.writeText(resetResult?.password || ""); toast.success("Copiada"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          {resetResult && (() => {
            // Fix N (2026-09-22): mensaje listo para mandarle a la persona por WhatsApp.
            const nombre = (resetResult.user?.name || "").replace(/\s*\(.*?\)\s*/g, " ").trim();
            const mensaje = [
              `Hola ${nombre}, ${resetResult.nuevo ? "ya tienes tu usuario" : "te restablecí la contraseña"} del panel de ${marca.nombre_comercial || "Marthita Shop"}.`,
              `Entra en: ${window.location.origin}/login`,
              `Correo: ${resetResult.user?.email || ""}`,
              `Contraseña temporal: ${resetResult.password}`,
              "Al entrar te va a pedir que la cambies por una tuya.",
            ].join("\n");
            return (
              <div>
                <Label className="text-xs">Mensaje para enviarle</Label>
                <Textarea readOnly rows={5} value={mensaje} className="rounded-xl mt-1 text-xs" data-testid="mensaje-acceso" />
                <Button size="sm" variant="outline" className="rounded-full mt-2 gap-1" onClick={() => { navigator.clipboard.writeText(mensaje); toast.success("Mensaje copiado"); }} data-testid="copiar-mensaje-acceso"><Copy className="h-4 w-4" /> Copiar mensaje</Button>
              </div>
            );
          })()}
          <DialogFooter><Button className="rounded-full bg-primary hover:bg-primary/90" onClick={() => setResetResult(null)}>Listo</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restaurar marca a valores por defecto */}
      <Dialog open={restaurarMarcaOpen} onOpenChange={setRestaurarMarcaOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Restaurar valores por defecto</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Esto vuelve el logo y los colores al valor original de Marthitashop. No se puede deshacer.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRestaurarMarcaOpen(false)}>Cancelar</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={restoreMarca} data-testid="marca-restaurar-confirm">Restaurar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
